import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
    adminCustomerService,
    AdminCustomer,
    GetAdminCustomersParams
} from "../../services/admin.customer.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserShield,
    faSearch,
    faCircleNotch,
    faTriangleExclamation,
    faAddressCard,
    faEnvelope,
    faPhone,
    faEye,
    faUserCheck,
    faUserSlash,
    faEdit,
    faTrash
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/pages/AdminCustomersPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import UpdateCustomerModal from "../../components/admin/UpdateCustomerModal";

const AdminCustomersPage: React.FC = () => {
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    useEffect(() => {
        if (!permissionCheckerService.checkPermission(adminData, "CUSTOMERS", "READ")) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [adminData, navigate]);

    const [customers, setCustomers] = useState<AdminCustomer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("");

    // Edit states
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<AdminCustomer | null>(null);

    const canReadOrders = permissionCheckerService.checkPermission(adminData, "ORDERS", "READ");
    const canChangeStatus = permissionCheckerService.checkPermission(adminData, "CUSTOMERS", "CHANGE STATUS");
    const canEditCustomer = permissionCheckerService.checkPermission(adminData, "CUSTOMERS", "EDIT");
    const canDeleteCustomer = permissionCheckerService.checkPermission(adminData, "CUSTOMERS", "DELETE");

    const fetchCustomers = async (page: number, size: number) => {
        try {
            setLoading(true);
            setError(null);

            const params: GetAdminCustomersParams = {
                page: page,
                pageSize: size,
                isActive: statusFilter === "" ? undefined : statusFilter === "true",
                SearchString: searchText || undefined,
            };

            const response = await adminCustomerService.getCustomers(params);

            if (response.statusCode === 200) {
                setCustomers(response.data);
                setTotalRows(response.pagination.totalCount);
            } else {
                setError(errorService.getDisplayMessage(response));
            }
        } catch (err: any) {
            console.error("Error fetching admin customers:", err);
            setError(errorService.getDisplayMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers(1, perPage);
    }, []);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchCustomers(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        fetchCustomers(page, newPerPage);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchCustomers(1, perPage);
    };

    const handleToggleActive = async (customer: AdminCustomer) => {
        const action = customer.isActive ? "desactivar" : "activar";
        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: `Esta acción procederá a ${action} al cliente ${customer.firstName} ${customer.lastName}.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: customer.isActive ? "#f59e0b" : "#10b981",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: `Sí, ${action}`,
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminCustomerService.updateCustomerActive({
                    IdCustomer: customer.id,
                    IsActive: !customer.isActive,
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: customer.isActive ? "Desactivado" : "Activado",
                        text: `El cliente ha sido ${customer.isActive ? "desactivado" : "activado"} con éxito.`,
                    });
                    fetchCustomers(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error updating customer active status:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteCustomer = async (customer: AdminCustomer) => {
        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: `Esta acción eliminará al cliente ${customer.firstName} ${customer.lastName}. Esta acción no se puede deshacer.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminCustomerService.deleteCustomer(customer.id);

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "Eliminado",
                        text: "El cliente ha sido eliminado con éxito.",
                    });
                    fetchCustomers(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error deleting customer:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setLoading(false);
            }
        }
    };

    const columns: TableColumn<AdminCustomer>[] = [
        {
            name: "Código cliente",
            selector: (row) => row.code || "",
            sortable: true,
            minWidth: "130px",
        },
        {
            name: "Cliente",
            selector: (row) => `${row.firstName} ${row.lastName}`,
            sortable: true,
            minWidth: "180px",
            cell: (row) => (
                <div className="d-flex flex-column py-2">
                    <span className={styles.customerName}>{row.firstName} {row.lastName}</span>
                </div>
            ),
        },
        {
            name: "Nro. documento",
            selector: (row) => row.dni,
            sortable: true,
            minWidth: "130px",
        },
        {
            name: "Teléfono",
            selector: (row) => row.phoneNumber,
            sortable: true,
            minWidth: "130px",
        },
        {
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
            minWidth: "200px",
        },
        {
            name: "Fecha registro",
            selector: (row) => row.createdAt,
            sortable: true,
            minWidth: "130px",
            cell: (row) => (
                <span>
                    {new Date(row.createdAt).toLocaleDateString("es-ES", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                    })}
                </span>
            ),
        },
        {
            name: "País",
            selector: (row) => row.isO2Country,
            sortable: true,
            width: "80px",
            center: true,
        },
        {
            name: "Estatus",
            selector: (row) => row.isActive ? 1 : 0,
            sortable: true,
            cell: (row) => (
                <div className="d-flex flex-column gap-1">
                    <span className={`${styles.badge} ${row.isActive ? styles.statusActive : styles.statusInactive}`}>
                        {row.isActive ? "Activo" : "Inactivo"}
                    </span>
                    <span className={`${styles.badge} ${row.isVerify ? styles.statusVerified : styles.statusUnverified}`}>
                        {row.isVerify ? "Verificado" : "Pendiente"}
                    </span>
                </div>
            ),
            width: "120px",
        },
        {
            name: "Acciones",
            width: "200px",
            cell: (row) => (
                <div className="d-flex gap-2">
                    <button
                        className={styles.detailButton}
                        onClick={() => navigate(`/admin/orders?customerEmail=${row.email}`)}
                        title="Ver pedidos"
                        disabled={!canReadOrders}
                    >
                        <FontAwesomeIcon icon={faEye} />
                    </button>
                    {canEditCustomer && (
                        <button
                            className={styles.detailButton}
                            onClick={() => {
                                setSelectedCustomer(row);
                                setShowEditModal(true);
                            }}
                            title="Editar cliente"
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}
                    {canChangeStatus && (
                        <button
                            className={row.isActive ? styles.deactivateButton : styles.activateButton}
                            onClick={() => handleToggleActive(row)}
                            title={row.isActive ? "Desactivar cliente" : "Activar cliente"}
                        >
                            <FontAwesomeIcon icon={row.isActive ? faUserSlash : faUserCheck} />
                        </button>
                    )}
                    {canDeleteCustomer && (
                        <button
                            className={styles.cancelButton}
                            onClick={() => handleDeleteCustomer(row)}
                            disabled={row.orderCount !== 0}
                            title={row.orderCount === 0 ? "Eliminar cliente" : "No se puede eliminar (tiene órdenes asociadas)"}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </div>
            ),
            center: true,
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "70px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.adminCustomersContainer}>
                <DashboardCard
                    icon={faUserShield}
                    title="Gestión de Clientes"
                    description="Visualiza y administra el historial y estado de los clientes registrados."
                />

                <div className={styles.tableWrapper}>
                    <form className={styles.tableActions} onSubmit={handleSearch}>
                        <div className={styles.filterSection}>
                            <div className={styles.filterGroup}>
                                <label>Estado de Cuenta</label>
                                <select
                                    className={styles.filterSelect}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="true">Activos</option>
                                    <option value="false">Inactivos</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.searchRow}>
                            <div className={styles.searchWrapper}>
                                <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre, email, DNI..."
                                    className={styles.searchInput}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                            <button type="submit" className={btnStyles.primary}>
                                <FontAwesomeIcon icon={faSearch} /> Buscar
                            </button>
                        </div>
                    </form>

                    {error ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                            <h3>Error de Carga</h3>
                            <p>{error}</p>
                            <button className={btnStyles.primary} style={{ margin: "20px auto" }} onClick={() => fetchCustomers(currentPage, perPage)}>
                                Reintentar
                            </button>
                        </div>
                    ) : customers.length === 0 && !loading ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faUserShield} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                            <p>No se encontraron clientes con los criterios de búsqueda.</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={customers}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationDefaultPage={currentPage}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            progressPending={loading}
                            progressComponent={
                                <div className={styles.loadingContainer}>
                                    <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                                    <p>Cargando lista de clientes...</p>
                                </div>
                            }
                            customStyles={customStyles}
                            responsive
                            highlightOnHover
                            noDataComponent={null}
                        />
                    )}
                </div>
            </div>

            <UpdateCustomerModal 
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedCustomer(null);
                }}
                onSuccess={() => fetchCustomers(currentPage, perPage)}
                customer={selectedCustomer}
            />
        </AdminDashboardLayout>
    );
};

export default AdminCustomersPage;
